
import { useEffect } from 'react';
import { useLoaderContext } from '../../../../components/Loader/LoaderContext';

import Header from '../../../dashboard/components/Header';
import ReportSidebar from '../../../AdminDashboard/AccessAdministration/pages/ReportSidebar';

import NtIdHeader from '../components/NtIdHeader';
import NtIdToolbar from '../components/NtIdToolbar';
import NtIdTable from '../components/NtIdTable';
import NtIdFormModal from '../components/NtIdFormModal';
import BulkImportModal from '../components/BulkImportModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import Toast from '../components/Toast';

import useNtIdManagement from '../hooks/useNtIdManagement';

const NtIdManagementPage = () => {
  const {
    loading,
    saving,
    error,

    employees,
    totalEmployees,

    search,
    setSearch,

    currentPage,
    pageSize,
    totalPages,
    setCurrentPage,
    setPageSize,

    form,

    roles,
    statuses,
    teams,
    shifts,
    reportingManagers,

    toast,
    setToast,

    selectedEmployee,

    showFormModal,
    showBulkModal,
    showDeleteModal,

    openAddModal,
    openEditModal,
    closeFormModal,

    openDeleteModal,
    closeDeleteModal,

    openBulkImportModal,
    closeBulkImportModal,

    saveEmployee,
    removeEmployee,

    handleFormChange,

    exportEmployees,
    bulkText,
    setBulkText,

    downloadTemplate,
    handleFileSelect,

    bulkImportEmployees,
  } = useNtIdManagement();

  const { hideLoader } = useLoaderContext();

  useEffect(() => {
    if (!loading) {
      hideLoader();
    }
  }, [loading, hideLoader]);

  return (
    <div className="min-h-screen bg-[#eef3f8] text-slate-900">
      {/* Existing WorkVista header */}
      <Header />

      <div className="flex min-h-[calc(100vh-72px)]">
        {/* New sidebar for WFM / Public Reports pages only */}
        <ReportSidebar />

        {/* NT ID Management content */}
        <main className="min-w-0 flex-1 p-4 sm:p-6">
          <div className="mx-auto w-full max-w-[1440px] space-y-6">
            <NtIdHeader
              onAdd={openAddModal}
              onBulkImport={openBulkImportModal}
              onExport={exportEmployees}
            />

            <NtIdToolbar
              search={search}
              totalEmployees={totalEmployees}
              onSearchChange={setSearch}
            />

            {error && (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {error}
              </div>
            )}

            <NtIdTable
              employees={employees}
              loading={loading}
              onEdit={openEditModal}
              onDelete={openDeleteModal}
              currentPage={currentPage}
              pageSize={pageSize}
              totalPages={totalPages}
              totalEmployees={totalEmployees}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />

            <NtIdFormModal
              open={showFormModal}
              saving={saving}
              isEdit={Boolean(selectedEmployee)}
              form={form}
              roles={roles}
              statuses={statuses}
              teams={teams}
              shifts={shifts}
              reportingManagers={reportingManagers}
              onChange={handleFormChange}
              onClose={closeFormModal}
              onSave={saveEmployee}
            />

            <BulkImportModal
              open={showBulkModal}
              saving={saving}
              value={bulkText}
              onTextChange={setBulkText}
              onClose={closeBulkImportModal}
              onImport={bulkImportEmployees}
              onDownloadTemplate={downloadTemplate}
              onUploadCsv={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.csv,text/csv';

                input.onchange = (event: Event) => {
                  const target = event.target as HTMLInputElement;
                  const file = target.files?.[0];

                  if (file) {
                    handleFileSelect(file);
                  }
                };

                input.click();
              }}
            />

            <DeleteConfirmationModal
              open={showDeleteModal}
              deleting={saving}
              employee={selectedEmployee}
              onClose={closeDeleteModal}
              onConfirm={removeEmployee}
            />

            <Toast
              open={toast.open}
              message={toast.message}
              type={toast.type}
              onClose={() =>
                setToast((previous) => ({
                  ...previous,
                  open: false,
                }))
              }
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default NtIdManagementPage;
